import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { UploadCloud, Loader2, Trash2, CheckCheck, FileStack, CheckCircle2, Heart, MessageCircle, Share2, Eye, Images } from "lucide-react";
import { fromJS } from "immutable";
import ReactCrop, { PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { base64ToFile } from "../helpers";
import { Modal, Button } from "../../components/ui";
import ImageView from "./ImageView";
import Canva from "../../icons/Canva";
import { getPlatformIcon, getPlatformStyle } from "../constants/platformIcons";
import CanvaPagesModal from "./CanvaPagesModal";
import MediaDetailModal, { MediaDetailItem } from "./MediaDetailModal";
import { getActivityFileExists, uploadFileLogActivity, deleteFileLogActivity } from "../../api/ProjectApi";
import { useMutation } from "@tanstack/react-query";
import AIContentAction from "../../actions/AIContentAction";
import LayoutAction from "../../actions/LayoutAction";
import { RefreshCw, Plus } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { getBaseUrl } from "../../api/api";
import {
  PLATFORM_BACKGROUND_COLOR,
  PLATFORM_IMAGE,
  PLATFORM_TEXT_COLOR_HEX,
  PLATFORM_TITLE,
} from "../constants/platform";
import CanvaPlus from "../../icons/CanvaPlus";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import { formatNumber } from "../../api/DataTypeUtils";
import MediaInsightsDailyChart from "./MediaInsightsDailyChart";

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "tiff"];
const VIDEO_EXTS = ["mp4", "mov", "avi", "mkv", "webm", "m4v", "flv"];
const ACCEPTED_EXTS = [...IMAGE_EXTS, ...VIDEO_EXTS];
const getMediaExt = (name: string) => (name.split(".").pop() || "").toLowerCase();

type MediaTabKey = "all" | "image" | "video" | "canva";

interface MediaGridProps {
  projectId: string;
  items: any[];
  canvaItems: any[];
}

const cardCls =
  "group relative cursor-pointer rounded-lg overflow-hidden bg-white transition-all duration-200 hover:shadow-[0_4px_20px_rgba(0,0,0,0.15)] hover:-translate-y-0.5";

/** Renders a single image/video card with dimension detection via onLoad */
const ImageCard: React.FC<{
  item: any;
  onClick: () => void;
  onDelete?: () => void;
  t: (k: string) => string;
}> = ({ item, onClick, onDelete, t }) => {
  const fileName = item?.event?.data?.fileName || item?.fileName || "";
  const itemId = item?.id || fileName;
  const ext = getMediaExt(fileName);
  const isVideo = VIDEO_EXTS.includes(ext);
  const shortName = fileName.split("/").pop() || fileName;
  const [imgDim, setImgDim] = useState<string | null>(null);

  const insights = item?.event?.data?.mediaInsights;
  const postCount: number = insights?.postCount ?? 0;
  const publishedCount: number = insights?.publishedCount ?? 0;
  const platforms: string[] = insights?.platforms ?? [];
  const hasInsights = postCount > 0;

  return (
    <div key={itemId} className={cardCls} onClick={onClick}>
      {/* Thumbnail */}
      <div className="aspect-[4/5] bg-gray-100 overflow-hidden relative rounded-lg">
        {isVideo ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gray-900">
            <svg className="w-10 h-10 text-white/70" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
            <span className="text-[11px] text-gray-400 uppercase tracking-wide">{ext}</span>
          </div>
        ) : (
          <ImageView
            fileName={fileName}
            className="w-full h-full transition-transform duration-200 group-hover:scale-[1.03]"
            onNaturalSize={(w, h) => {
              if (w && h) setImgDim(`${w} × ${h} px`);
            }}
          />
        )}
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all duration-200 rounded-lg" />
        {/* File type badge — top right */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <span className="bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded">
            {ext || "file"}
          </span>
        </div>
        {/* Canva source badge — top left */}
        {item?.event?.data?.source === "Canva" && (
          <div className="absolute top-2 left-2">
            <div className="w-6 h-6 rounded-full bg-white/90 backdrop-blur-sm shadow flex items-center justify-center">
              <Canva size={14} />
            </div>
          </div>
        )}
        {/* Delete button — bottom left on hover */}
        {onDelete && (
          <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              className="w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-red-50 text-gray-500 hover:text-red-600 rounded-lg shadow-sm transition-colors"
              title={t("Delete")}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {/* Card footer */}
      <div className="pt-2 pb-1.5 px-0.5">
        <p className="text-sm font-medium text-gray-800 truncate leading-snug" title={shortName}>
          {shortName}
        </p>
        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
          <span>{imgDim || (isVideo ? t("Video") : t("Image"))}</span>
        </p>
        {/* Media insights row */}
        {hasInsights ? (
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-600 bg-blue-50 rounded-full px-1.5 py-0.5"
              title={`${postCount} post${postCount !== 1 ? "s" : ""} using this media`}
            >
              <FileStack className="w-2.5 h-2.5" />
              {postCount}
            </span>
            {publishedCount > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium text-green-600 bg-green-50 rounded-full px-1.5 py-0.5"
                title={`${publishedCount} post${publishedCount !== 1 ? "s" : ""} published`}
              >
                <CheckCheck className="w-2.5 h-2.5" />
                {publishedCount}
              </span>
            )}
            {platforms.length > 0 && (
              <div
                className="flex items-center"
                title={platforms.map((p: string) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(", ")}
              >
                {platforms.slice(0, 5).map((p: string, i: number) => {
                  const PIcon = getPlatformIcon(p);
                  const style = getPlatformStyle(p);
                  return (
                    <span
                      key={p}
                      className="w-4 h-4 rounded-full flex items-center justify-center ring-1 ring-white flex-shrink-0"
                      style={{ background: style.color, marginLeft: i === 0 ? 0 : "-4px", zIndex: platforms.length - i }}
                    >
                      <PIcon className="w-2 h-2 text-white" />
                    </span>
                  );
                })}
                {platforms.length > 5 && (
                  <span className="w-4 h-4 rounded-full flex items-center justify-center ring-1 ring-white bg-gray-300 text-[7px] font-bold text-gray-600 -ml-1">
                    +{platforms.length - 5}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-1.5 h-[18px]" />
        )}
      </div>
    </div>
  );
};

const MediaGrid: React.FC<MediaGridProps> = ({ projectId, items, canvaItems }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mediaTab, setMediaTab] = React.useState<MediaTabKey>("all");
  const [selectedMedia, setSelectedMedia] = React.useState<MediaDetailItem | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmExistsImage, setShowConfirmExistsImage] = useState(false);
  const [pendingDeleteMedia, setPendingDeleteMedia] = useState<{ id: string; fileName: string } | null>(null);

  const fileImages = fromJS(items);

  const deleteMediaMutation = useMutation({
    mutationFn: (activityId: string) => deleteFileLogActivity(activityId),
    onSuccess: () => {
      setPendingDeleteMedia(null);
      dispatch(AIContentAction.getFileImages(projectId) as any);
    },
    onError: (error: any) => {
      setPendingDeleteMedia(null);
      const status = (error as any)?.response?.status;
      const message = status === 409
        ? t("Cannot delete this media because it is used in one or more posts.")
        : t("Failed to delete media.");
      dispatch(LayoutAction.setNotification({ title: t("Delete Media"), type: "error", message }) as any);
    },
  });

  const tabCounts = {
    all: items.length,
    image: items.filter((i: any) =>
      IMAGE_EXTS.includes(getMediaExt(i?.event?.data?.fileName || i?.fileName || ""))
    ).length,
    video: items.filter((i: any) =>
      VIDEO_EXTS.includes(getMediaExt(i?.event?.data?.fileName || i?.fileName || ""))
    ).length,
    canva: canvaItems.length,
  };

  const TABS: { key: MediaTabKey; label: string; icon?: React.ReactNode }[] = [
    { key: "all", label: t("All") },
    { key: "image", label: t("Image") },
    { key: "video", label: t("Video") },
    { key: "canva", label: "Canva", icon: <Canva color="currentColor" /> },
  ];

  const filtered = items.filter((item: any) => {
    const ext = getMediaExt(item?.event?.data?.fileName || item?.fileName || "");
    if (mediaTab === "image") return IMAGE_EXTS.includes(ext);
    if (mediaTab === "video") return VIDEO_EXTS.includes(ext);
    return true;
  });

  const mediaInsights = useMemo(() => {
    const postCount = fileImages
      .reduce((sum, item) => sum + (item.getIn(["event", "data", "mediaInsights", "postCount"]) || 0), 0);
    const publishedCount = fileImages
      .reduce((sum, item) => sum + (item.getIn(["event", "data", "mediaInsights", "publishedCount"]) || 0), 0);
    const totalLikeCount = fileImages
      .reduce((sum, item) => sum + (item.getIn(["event", "data", "mediaInsights", "totalLikeCount"]) || 0), 0);
    const totalCommentCount = fileImages
      .reduce((sum, item) => sum + (item.getIn(["event", "data", "mediaInsights", "totalCommentCount"]) || 0), 0);
    const totalShareCount = fileImages
      .reduce((sum, item) => sum + (item.getIn(["event", "data", "mediaInsights", "totalShareCount"]) || 0), 0);
    const totalViewCount = fileImages
      .reduce((sum, item) => sum + (item.getIn(["event", "data", "mediaInsights", "totalViewCount"]) || 0), 0);
    return [
      { label: t("Posts"), value: postCount, icon: Images },
      { label: t("Published"), value: publishedCount, icon: CheckCircle2 },
      { label: t("Likes"), value: totalLikeCount, icon: Heart },
      { label: t("Comments"), value: totalCommentCount, icon: MessageCircle },
      { label: t("Shares"), value: totalShareCount, icon: Share2 },
      { label: t("Views"), value: totalViewCount, icon: Eye },
    ];
  }, [fileImages, t]);

  /* ── Crop State ── */
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropOriginalFile, setCropOriginalFile] = useState<File | null>(null);
  const [crop, setCrop] = useState<PixelCrop>({ unit: "px", width: 1080, height: 1080, x: 0, y: 0 });
  const [croppedBase64, setCroppedBase64] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  /* ── Canva State ── */
  const [selectedCanvaDesign, setSelectedCanvaDesign] = useState<{ id: string; title: string } | null>(null);
  const [isLoadingCanvaDesigns, setIsLoadingCanvaDesigns] = useState(false);

  const renderPlatformDesign = (platform: string) => {
    let url;
    switch (platform) {
      case "instagram":
        url = "https://www.canva.com/design?create&type=TAEby5mLXEk&category=tACFajEYUAM&ui=e30";
        break;
      case "facebook":
        url = "https://www.canva.com/design?create&type=TACQ-rUo-uE&category=tACFavw5Wlg&ui=e30";
        break;
      case "linkedin":
        url = "https://www.canva.com/design?create&type=TAEHQ-OPEx0&category=tAEL9-BMBAg&ui=e30";
        break;
      case "twitter":
        url = "https://www.canva.com/design?create&type=TAD-2D3JRN4&category=tACFagP1mXg&ui=e30";
        break;
      case "threads":
        url = "https://www.canva.com/design?create&type=TAFoNQA2s7M&category=tAFoNX-UKHw&ui=e30";
        break;
    }
    return (
      <div
        className="w-full h-full py-1 border border-gray-200 rounded-lg bg-gray-50 space-y-1 cursor-pointer"
        onClick={() => {
          if (url) window.open(url, "_blank");
        }}
      >
        <div className="flex items-center justify-center gap-1">
          <div className="p-1">
            <img
              src={`${getBaseUrl()}${PLATFORM_IMAGE[platform]}`}
              className="h-5"
              alt={platform}
            />
          </div>
          <div className={`w-16 h-2 rounded-xl ${PLATFORM_BACKGROUND_COLOR[platform]}`}></div>
        </div>
        <div
          className={`w-full h-10 flex items-center justify-center ${PLATFORM_BACKGROUND_COLOR[platform]}`}
        >
          <CanvaPlus size={12} color={PLATFORM_TEXT_COLOR_HEX[platform]} />
        </div>
        <div className="px-2 text-xs font-semibold text-center">
          {`${PLATFORM_TITLE[platform]} Post`}
        </div>
      </div>
    );
  };

  const compressCanvas = async (image: HTMLImageElement, crop: PixelCrop, maxSizeBytes: number) => {
    let quality = 0.92;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    let width = crop.width * scaleX;
    let height = crop.height * scaleY;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    while (true) {
      canvas.width = Math.round(width);
      canvas.height = Math.round(height);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
      const base64 = canvas.toDataURL("image/jpeg", quality);
      const sizeBytes = Math.ceil((base64.length * 3) / 4);
      if (sizeBytes <= maxSizeBytes) return base64;
      if (quality > 0.5) {
        quality -= 0.07;
      } else {
        width *= 0.9;
        height *= 0.9;
      }
      if (width < 1080 || height < 1080) return base64;
    }
  };

  const makeClientCrop = async (crop: PixelCrop) => {
    if (imgRef.current && crop.width && crop.height) {
      const base64 = await compressCanvas(imgRef.current, crop, 3 * 1024 * 1024);
      if (base64) setCroppedBase64(base64);
    }
  };

  const onImageLoaded = (img: HTMLImageElement) => {
    imgRef.current = img;
    const defaultCrop = {
      unit: "px",
      width: img.width,
      height: img.height,
      x: 0,
      y: 0,
    };
    setCrop(defaultCrop as PixelCrop);
    setTimeout(() => makeClientCrop(defaultCrop as PixelCrop), 0);
  };

  const handleConfirmCrop = async () => {
    if (!croppedBase64 || !cropOriginalFile) return;
    setIsUploading(true);
    try {
      const finalFile = base64ToFile(croppedBase64, cropOriginalFile.name);
      await uploadFileLogActivity("", finalFile, "Shared", { source: "User" });
      dispatch(AIContentAction.getFileImages(projectId) as any);
      dispatch(
        LayoutAction.setNotification({
          title: t("Upload"),
          type: "success",
          message: t("Image uploaded successfully!"),
        })
      );
    } catch {
      dispatch(
        LayoutAction.setNotification({
          title: t("Upload"),
          type: "error",
          message: t("Failed to upload image."),
        })
      );
    } finally {
      setIsUploading(false);
      setCropSrc(null);
      setCropOriginalFile(null);
      setCroppedBase64(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCancelCrop = () => {
    setCropSrc(null);
    setCropOriginalFile(null);
    setCroppedBase64(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ── Upload handler ── */
  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // If single image (and not gif/svg), open crop modal
    if (files.length === 1) {
      const file = files[0];
      if (file.type.startsWith("image/") && !file.type.includes("svg") && !file.type.includes("gif")) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setCropSrc(ev.target?.result as string);
          setCropOriginalFile(file);
        };
        reader.readAsDataURL(file);
        return; // wait for crop confirm
      }
    }

    // Direct multi-file or video upload
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = getMediaExt(file.name);
        if (!ACCEPTED_EXTS.includes(ext)) {
          dispatch(
            LayoutAction.setNotification({
              title: t("Upload"),
              type: "error",
              message: t("Unsupported file type: {{ext}}", { ext }),
            })
          );
          continue;
        }
        await uploadFileLogActivity("", file, "Shared", { source: "User" });
      }
      dispatch(AIContentAction.getFileImages(projectId) as any);
      dispatch(
        LayoutAction.setNotification({
          title: t("Upload"),
          type: "success",
          message: t("File uploaded successfully!"),
        })
      );
    } catch {
      dispatch(
        LayoutAction.setNotification({
          title: t("Upload"),
          type: "error",
          message: t("Failed to upload file."),
        })
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUploadImage = async () => {
    if (!croppedBase64 || !cropOriginalFile) return;
    const existImage = await getActivityFileExists(cropOriginalFile.name);
    if (existImage.exists) {
      setShowConfirmExistsImage(true);
    } else {
      await handleConfirmCrop();
    }
  };

  const handleSubmitExistsImage = async () => {
    await handleConfirmCrop();
    setShowConfirmExistsImage(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleUpload(e.dataTransfer.files);
  };

  /* ── Drop zone (only shown when not on Canva tab) ── */
  const DropZone = () => (
    <div
      className={`mb-6 flex items-center justify-center gap-3 px-6 py-5 rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer select-none ${
        isDragging
          ? "border-[#2563EB] bg-blue-50 scale-[1.01]"
          : "border-gray-200 bg-gray-50 hover:border-[#2563EB] hover:bg-blue-50"
      }`}
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
      onClick={() => !isUploading && fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTS.map((e) => `.${e}`).join(",")}
        className="hidden"
        onChange={(e) => handleUpload(e.target.files)}
      />
      {isUploading ? (
        <>
          <Loader2 className="w-5 h-5 text-[#2563EB] animate-spin" />
          <span className="text-sm font-medium text-[#2563EB]">{t("Uploading…")}</span>
        </>
      ) : (
        <>
          <UploadCloud className={`w-5 h-5 ${isDragging ? "text-[#2563EB]" : "text-gray-400"}`} />
          <span className={`text-sm font-medium ${isDragging ? "text-[#2563EB]" : "text-gray-500"}`}>
            {t("Drop a file to upload or click to select")}
          </span>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="mb-3 flex items-stretch divide-x divide-gray-200 border border-gray-200 rounded-md bg-white overflow-hidden">
        {mediaInsights.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="flex-1 flex items-center gap-2.5 px-3 py-2.5 min-w-0">
              <Icon className="h-4 w-4 text-gray-400 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 leading-none truncate">{item.label}</p>
                <p className="text-base font-bold text-[#2563EB] leading-snug">{formatNumber(item.value)}</p>
              </div>
            </div>
          );
        })}
      </div>

      <MediaInsightsDailyChart />

      {/* ── Tab bar ── */}
      <div className="flex items-center gap-1 mb-5 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setMediaTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              mediaTab === tab.key
                ? "border-[#2563EB] text-[#2563EB]"
                : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
            }`}
          >
            {tab.icon && <span className="w-3.5 h-3.5 flex items-center">{tab.icon}</span>}
            {tab.label}
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                mediaTab === tab.key
                  ? "bg-blue-100 text-[#2563EB]"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {tabCounts[tab.key]}
            </span>
          </button>
        ))}
      </div>

      {/* ── Canva grid ── */}
      {mediaTab === "canva" ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between bg-white px-2 py-1">
            <div className="text-sm font-semibold text-gray-800">{t("Design List")}</div>
            <div className="flex items-center gap-1">
              <button
                className="p-1 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
                onClick={async () => {
                  setIsLoadingCanvaDesigns(true);
                  await dispatch(AIContentAction.getCanvaDesigns());
                  setIsLoadingCanvaDesigns(false);
                }}
                title={t("Refresh")}
                disabled={isLoadingCanvaDesigns}
              >
                <RefreshCw
                  className={`w-4 h-4 text-gray-400 ${isLoadingCanvaDesigns ? "animate-spin" : ""}`}
                />
              </button>
              <Popover className="relative">
                <PopoverButton
                  className="p-1 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer outline-none"
                  title={t("Create Design")}
                >
                  <Plus className="w-5 h-5 text-gray-400" />
                </PopoverButton>
                <PopoverPanel
                  anchor="bottom end"
                  className="z-[9999] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 p-4 bg-white rounded-md border border-gray-300 shadow-md w-max"
                >
                  {renderPlatformDesign("instagram")}
                  {renderPlatformDesign("facebook")}
                  {renderPlatformDesign("linkedin")}
                  {renderPlatformDesign("twitter")}
                  {renderPlatformDesign("threads")}
                </PopoverPanel>
              </Popover>
            </div>
          </div>
          {canvaItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center pt-20 gap-3">
              <img src={`${getBaseUrl()}/static/images/empty-state.png`} className="h-[83px] object-contain" alt="Empty" />
              <div className="text-gray-500 text-sm">{t("No Canva designs found")}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {canvaItems.map((item: any) => {
                const id = item?.id || "";
                const title = item?.title || t("Untitled");
                const thumbnailUrl = item?.thumbnail?.url || "";
                const canvaDims = item?.dimensions;
                const canvaDimLabel = canvaDims?.width && canvaDims?.height
                  ? `${Math.round(canvaDims.width)} × ${Math.round(canvaDims.height)} px`
                  : null;
                return (
                  <div key={id} className={cardCls}>
                    {/* Thumbnail */}
                    <div className="aspect-[4/5] bg-gray-100 overflow-hidden relative rounded-lg">
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={title}
                          className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-purple-50">
                          <Canva color="#7d2ae8" />
                        </div>
                      )}

                      {/* Dark overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 rounded-lg" />

                      {/* Hover action buttons */}
                      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-1.5 pb-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-200">
                        <button
                          className="w-[calc(100%-16px)] py-1.5 bg-[#2563eb] hover:bg-blue-700 text-white text-xs font-semibold rounded-md shadow-md transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`https://www.canva.com/design/${id}/edit`, "_blank");
                          }}
                        >
                          {t("Edit")}
                        </button>
                        <button
                          className="w-[calc(100%-16px)] py-1.5 bg-white hover:bg-gray-50 text-gray-800 text-xs font-semibold rounded-md shadow-md border border-gray-200 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCanvaDesign({ id, title });
                          }}
                        >
                          {t("Use")}
                        </button>
                      </div>

                      {/* Canva badge — top left */}
                      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="bg-white/90 backdrop-blur-sm rounded-md px-1.5 py-0.5 shadow-sm flex items-center gap-1">
                          <Canva color="#7d2ae8" />
                        </div>
                      </div>
                    </div>

                    {/* Card footer */}
                    <div className="pt-2 pb-1.5 px-0.5">
                      {thumbnailUrl ? (
                        <>
                          <p className="text-sm font-medium text-gray-800 truncate leading-snug" title={title}>
                            {title}
                          </p>
                          {canvaDimLabel ? (
                            <p className="text-xs text-gray-400 mt-0.5">{canvaDimLabel}</p>
                          ) : (
                            <p className="text-xs text-gray-400 mt-0.5">Canva design</p>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="skeleton-shimmer h-3 rounded-full w-4/5 mt-0.5" />
                          <div className="skeleton-shimmer h-2.5 rounded-full w-2/5 mt-1.5" />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ── Media (All / Image / Video) grid ── */
        <>
          <DropZone />

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center pt-16 gap-3">
              <img src={`${getBaseUrl()}/static/images/empty-state.png`} className="h-[83px] object-contain" alt="Empty" />
              <div className="text-gray-500 text-sm">{t("No media files uploaded yet")}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {filtered.map((item: any) => {
                const fileName = item?.event?.data?.fileName || item?.fileName || "";
                const itemId = item?.id || fileName;
                const isVideo = VIDEO_EXTS.includes(getMediaExt(fileName));
                return (
                  <ImageCard
                    key={itemId}
                    item={item}
                    t={t}
                    onClick={() =>
                      setSelectedMedia({
                        id: itemId,
                        fileName,
                        createdAt: item?.event?.createdAt || item?.createdAt,
                        isVideo,
                        fileSize: item?.event?.data?.fileSize ?? item?.event?.data?.size,
                        width: item?.event?.data?.width,
                        height: item?.event?.data?.height,
                        source: item?.event?.data?.source,
                        fileContentType: item?.event?.data?.fileContentType,
                        visibility: item?.event?.data?.visibility,
                        mediaInsights: item?.event?.data?.mediaInsights,
                      })
                    }
                    onDelete={() => setPendingDeleteMedia({ id: itemId, fileName })}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Crop Modal */}
      {cropSrc && (
        <Modal
          isOpen={true}
          onClose={handleCancelCrop}
          title={t("Crop Image")}
          className="w-[600px] max-w-[95vw]"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="w-full max-h-[60vh] overflow-auto flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={makeClientCrop}
              >
                <img
                  src={cropSrc}
                  onLoad={(e) => onImageLoaded(e.currentTarget)}
                  alt="Crop preview"
                  className="max-w-full"
                />
              </ReactCrop>
            </div>
            <div className="flex w-full justify-end gap-2 mt-2">
              <Button variant="ghost" onClick={handleCancelCrop} disabled={isUploading}>
                {t("Cancel")}
              </Button>
              <Button
                variant="primary"
                onClick={handleUploadImage}
                isLoading={isUploading}
              >
                {t("Upload Image")}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Canva Pages Modal */}
      {selectedCanvaDesign && (
        <CanvaPagesModal
          designId={selectedCanvaDesign.id}
          designTitle={selectedCanvaDesign.title}
          onClose={() => setSelectedCanvaDesign(null)}
          onExportImage={async (filename, file, meta) => {
            await uploadFileLogActivity("", file, "Shared", { ...meta, source: "Canva" });
            dispatch(AIContentAction.getFileImages(projectId) as any);
            dispatch(
              LayoutAction.setNotification({
                title: t("Success"),
                type: "success",
                message: t("Exported to media library!"),
              })
            );
            setSelectedCanvaDesign(null);
            setMediaTab("all");
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedMedia && (
        <MediaDetailModal
          projectId={projectId}
          item={selectedMedia}
          onClose={() => setSelectedMedia(null)}
          onDelete={() => {
            setSelectedMedia(null);
            dispatch(AIContentAction.getFileImages(projectId) as any);
          }}
          onInsightsChanged={() => {
            dispatch(AIContentAction.getFileImages(projectId) as any);
          }}
        />
      )}

      <ConfirmDialog
        visible={showConfirmExistsImage}
        title={t("Upload Image")}
        description={t(
          "The image already exists. Are you sure you want to overwrite the existing image?"
        )}
        alertIcon={<TriangleAlertIcon />}
        onSubmit={handleSubmitExistsImage}
        onClose={() => {
          setShowConfirmExistsImage(false);
        }}
        dangerBtn={true}
      />
      <ConfirmDialog
        visible={!!pendingDeleteMedia}
        title={t("Delete Media")}
        description={t("This media file will be permanently deleted. This action cannot be undone.")}
        dangerBtn
        submitBtnLabel={t("Delete")}
        submitting={deleteMediaMutation.isPending}
        onClose={() => setPendingDeleteMedia(null)}
        onSubmit={() => pendingDeleteMedia && deleteMediaMutation.mutateAsync(pendingDeleteMedia.id)}
      />
    </>
  );
};

export default MediaGrid;
